import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SettingsDialog = ({ onClose }) => {
  const [tokens, setTokens] = useState([]);
  const [newTokenName, setNewTokenName] = useState('Chrome Extension');
  const [loading, setLoading] = useState(false);
  const [newToken, setNewToken] = useState(null);
  
  const { theme, setTheme, isDark } = useTheme();
  const { language, setLanguage, languages, t } = useLanguage();

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/tokens`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTokens(data);
      }
    } catch (error) {
      console.error('Failed to load tokens:', error);
    }
  };

  const handleCreateToken = async () => {
    if (!newTokenName.trim()) {
      toast.error(t('token_name'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newTokenName })
      });

      if (response.ok) {
        const data = await response.json();
        setNewToken(data.token);
        setNewTokenName('');
        loadTokens();
        toast.success(t('token_created'));
      } else {
        throw new Error('Failed to create token');
      }
    } catch (error) {
      console.error('Failed to create token:', error);
      toast.error(t('error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteToken = async (tokenId) => {
    if (!window.confirm(t('delete_token_confirm'))) return;

    try {
      const response = await fetch(`${API_URL}/api/auth/tokens/${tokenId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(t('token_deleted'));
        loadTokens();
      } else {
        throw new Error('Failed to delete token');
      }
    } catch (error) {
      console.error('Failed to delete token:', error);
      toast.error(t('error_occurred'));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('copied'));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      <div className="relative bg-card rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">{t('settings')}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent/10 rounded-lg transition-colors"
            data-testid="close-settings"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Theme Section */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
              {isDark ? (
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
              {t('theme')}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => setTheme('light')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                  theme === 'light'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-secondary/50 text-foreground border-border hover:bg-secondary'
                }`}
                data-testid="theme-light"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                {t('theme_light')}
              </button>
              <button
                onClick={() => setTheme('dark')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                  theme === 'dark'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-secondary/50 text-foreground border-border hover:bg-secondary'
                }`}
                data-testid="theme-dark"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                {t('theme_dark')}
              </button>
            </div>
          </div>

          {/* Language Section */}
          <div className="mb-6 pb-6 border-b border-border">
            <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              {t('language')}
            </h3>
            <div className="flex gap-2 flex-wrap">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                    language === lang.code
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-secondary/50 text-foreground border-border hover:bg-secondary'
                  }`}
                  data-testid={`language-${lang.code}`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="text-sm">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Chrome Extension Section */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              {t('chrome_extension')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('chrome_extension_desc')}
            </p>
            
            <div className="bg-secondary/50 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-medium mb-2">{t('how_to_install')}</h4>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>{t('install_step_1')}</li>
                <li>{t('install_step_2')}</li>
                <li>{t('install_step_3')}</li>
                <li>{t('install_step_4')}</li>
                <li>{t('install_step_5')}</li>
              </ol>
            </div>

            <a
              href={`${API_URL}/api/extension/download`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              data-testid="download-extension"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              {t('download_extension')}
            </a>
          </div>

          {/* API Tokens Section */}
          <div className="border-t border-border pt-6">
            <h3 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              {t('api_tokens')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('api_tokens_desc')}
            </p>

            {/* New Token Display */}
            {newToken && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                  🔑 {t('token_created')}
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white dark:bg-black/20 px-3 py-2 rounded text-sm font-mono truncate">
                    {newToken}
                  </code>
                  <button
                    onClick={() => copyToClipboard(newToken)}
                    className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                  >
                    {t('copy')}
                  </button>
                </div>
                <button
                  onClick={() => setNewToken(null)}
                  className="text-sm text-green-700 dark:text-green-300 mt-2 hover:underline"
                >
                  {t('close')}
                </button>
              </div>
            )}

            {/* Create Token Form */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                placeholder={t('token_name')}
                className="flex-1 px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background"
                data-testid="token-name-input"
              />
              <button
                onClick={handleCreateToken}
                disabled={loading}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50"
                data-testid="create-token-button"
              >
                {loading ? '...' : t('create_token')}
              </button>
            </div>

            {/* Tokens List */}
            <div className="space-y-2">
              {tokens.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('no_tokens')}
                </p>
              ) : (
                tokens.map((token) => (
                  <div
                    key={token.token_id}
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">{token.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(token.created_at).toLocaleDateString()}
                        {token.last_used && ` • ${new Date(token.last_used).toLocaleDateString()}`}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteToken(token.token_id)}
                      className="p-2 hover:bg-destructive/10 rounded-lg transition-colors text-destructive"
                      title={t('delete')}
                      data-testid={`delete-token-${token.token_id}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsDialog;
